import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BadgeDollarSign, Edit2, Plus, X, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { fmtMoney, SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from "../lib/money";
import { useWorkspace } from "../lib/WorkspaceContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const KINDS = ["financial", "operational", "legal", "business"];

const KIND_TONES = {
  financial: "text-emerald-700 bg-emerald-50 border-emerald-200",
  legal: "text-rose-700 bg-rose-50 border-rose-200",
  operational: "text-sky-700 bg-sky-50 border-sky-200",
  business: "text-violet-700 bg-violet-50 border-violet-200",
};

/**
 * Inline editor for `alert.estimated_impact`.
 * PATCH /api/alm/alerts/{id}/impact  body={estimated_impact:{...}}
 *
 * UpdateImpactBody only accepts `estimated_impact` — no title change.
 */
export default function ImpactEditor({ alert, onUpdated }) {
  const { t } = useTranslation("components");
  const { current } = useWorkspace();
  const workspaceCurrency = current?.currency || DEFAULT_CURRENCY;
  const existing = alert.estimated_impact || null;
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState(existing?.kind || "financial");
  const [amount, setAmount] = useState(
    existing?.amount != null ? String(existing.amount) : "",
  );
  const [currency, setCurrency] = useState(
    existing?.currency ? String(existing.currency).toUpperCase() : workspaceCurrency,
  );
  const [description, setDescription] = useState(existing?.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const openEditor = () => {
    setKind(existing?.kind || "financial");
    setAmount(existing?.amount != null ? String(existing.amount) : "");
    setCurrency(existing?.currency ? String(existing.currency).toUpperCase() : workspaceCurrency);
    setDescription(existing?.description || "");
    setError(null);
    setEditing(true);
  };

  const closeEditor = () => setEditing(false);

  const handleSave = async () => {
    setError(null);
    const desc = description.trim();
    if (desc.length < 1) {
      setError(t("alert_drawer.impact.error_description_required"));
      return;
    }
    const payload = {
      estimated_impact: {
        kind,
        description: desc,
      },
    };
    const parsed = amount === "" ? null : parseFloat(amount);
    if (parsed !== null && !Number.isNaN(parsed)) {
      payload.estimated_impact.amount = parsed;
      payload.estimated_impact.currency = currency;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/alm/alerts/${alert.id}/impact`, payload);
      toast.success(t("alert_drawer.impact.save_success"));
      onUpdated?.(res.data);
      setEditing(false);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
        t("alert_drawer.impact.save_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        className="mt-4 border-t border-zinc-100 pt-4 space-y-3"
        data-testid="impact-editor"
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            {t("alert_drawer.impact.editor_title")}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={closeEditor}
            className="h-6 w-6 p-0"
            data-testid="impact-editor-close"
            aria-label={t("alert_drawer.impact.close")}
          >
            <X size={14} />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              {t("alert_drawer.impact.kind")}
            </Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="impact-editor-kind-trigger" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`impact-editor-kind-option-${k}`}>
                    {t(`alert_drawer.impact.kind_${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              {t("alert_drawer.impact.currency")}
            </Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="impact-editor-currency-trigger" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`impact-editor-currency-option-${c}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
            {t("alert_drawer.impact.amount")}
          </Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t("alert_drawer.impact.amount_placeholder")}
            data-testid="impact-editor-amount"
            className="h-8 text-sm font-mono tabular-nums"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
            {t("alert_drawer.impact.description")}
          </Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("alert_drawer.impact.description_placeholder")}
            rows={2}
            maxLength={500}
            data-testid="impact-editor-description"
            className="text-sm"
          />
        </div>

        {error && (
          <div
            className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5"
            data-testid="impact-editor-error"
          >
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="impact-editor-save"
          >
            <Save size={12} className="mr-1" />
            {saving ? t("alert_drawer.impact.saving") : t("alert_drawer.impact.save")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={closeEditor}
            data-testid="impact-editor-cancel"
          >
            {t("alert_drawer.impact.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  // Read mode — display + trigger
  return (
    <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="impact-view">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          {t("alert_drawer.impact.section_title")}
        </p>
        {existing ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openEditor}
            className="h-6 text-[11px] px-2"
            data-testid="impact-edit-button"
          >
            <Edit2 size={11} className="mr-1" />
            {t("alert_drawer.impact.edit")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openEditor}
            className="h-6 text-[11px] px-2"
            data-testid="impact-add-button"
          >
            <Plus size={11} className="mr-1" />
            {t("alert_drawer.impact.add")}
          </Button>
        )}
      </div>

      {existing ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid="impact-view-body">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${KIND_TONES[existing.kind] || ""}`}>
            {t(`alert_drawer.impact.kind_${existing.kind}`, existing.kind)}
          </span>
          {fmtMoney(existing.amount, existing.currency) && (
            <span
              className="font-mono tabular-nums text-sm font-semibold text-zinc-950 inline-flex items-center gap-1"
              data-testid="impact-view-amount"
            >
              <BadgeDollarSign size={12} />
              {fmtMoney(existing.amount, existing.currency)}
            </span>
          )}
          {existing.description && (
            <p className="text-xs text-zinc-700 leading-snug w-full">
              {existing.description}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-zinc-400 italic" data-testid="impact-view-empty">
          {t("alert_drawer.impact.empty")}
        </p>
      )}
    </div>
  );
}
